import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

import { ButtonComponent } from '../../../../shared/ui/button/button.component';

/**
 * Aviso informativo NO bloqueante al elegir Uber Flash como método de envío.
 * A diferencia de `BlockingNoticeModalComponent` (apartado/comprobante), no
 * exige checkbox de lectura: un único CTA ("Entendido") cierra, y también se
 * puede descartar con Escape o clic en el backdrop — nunca impide continuar
 * el checkout.
 */
@Component({
  selector: 'app-uber-flash-notice-modal',
  imports: [ButtonComponent],
  templateUrl: './uber-flash-notice-modal.component.html',
  styleUrl: './uber-flash-notice-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UberFlashNoticeModalComponent {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  readonly open = input<boolean>(false);
  readonly title = input<string>('');
  readonly body = input<string>('');
  readonly whatsappUrl = input<string>('');
  readonly whatsappLabel = input<string>('');
  readonly ctaLabel = input<string>('Entendido');
  readonly mascotSrc = input<string>('/images/logo/info.png');
  readonly mascotWidth = input<number>(481);
  readonly mascotHeight = input<number>(640);
  readonly acknowledge = output<void>();

  private readonly dialogRef = viewChild<ElementRef<HTMLElement>>('dialog');
  private previouslyFocusedElement: HTMLElement | null = null;
  private previousBodyOverflow = '';
  private bodyScrollLocked = false;

  constructor() {
    // Guarda/restaura foco + bloqueo de scroll, según `open`.
    effect(() => {
      const isOpen = this.open();
      if (!isPlatformBrowser(this.platformId)) return;

      if (isOpen) {
        if (!this.bodyScrollLocked) {
          this.previousBodyOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          this.bodyScrollLocked = true;
        }
        this.previouslyFocusedElement = (document.activeElement as HTMLElement | null) ?? null;
        queueMicrotask(() => {
          const root = this.dialogRef()?.nativeElement;
          if (root) this.getFocusableElements(root)[0]?.focus();
        });
      } else {
        this.restoreBodyScroll();
        if (this.previouslyFocusedElement) {
          const el = this.previouslyFocusedElement;
          this.previouslyFocusedElement = null;
          queueMicrotask(() => {
            if (document.contains(el)) el.focus({ preventScroll: true });
          });
        }
      }
    });

    // Si se abandona el checkout con el modal abierto, no deja el body bloqueado.
    this.destroyRef.onDestroy(() => this.restoreBodyScroll());

    // Focus trap NO bloqueante: Escape sí cierra (a diferencia de blocking-notice-modal).
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (!this.open()) return;

          if (e.key === 'Escape') {
            e.preventDefault();
            this.onAcknowledge();
            return;
          }

          if (e.key !== 'Tab') return;

          const modal = this.dialogRef()?.nativeElement;
          if (!modal) return;

          const focusables = this.getFocusableElements(modal);
          if (focusables.length === 0) {
            e.preventDefault();
            return;
          }

          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;

          if (!active || !modal.contains(active)) {
            e.preventDefault();
            first.focus();
            return;
          }

          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        });
    });
  }

  protected onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) this.onAcknowledge();
  }

  protected onAcknowledge(): void {
    this.acknowledge.emit();
  }

  private restoreBodyScroll(): void {
    if (!isPlatformBrowser(this.platformId) || !this.bodyScrollLocked) return;
    document.body.style.overflow = this.previousBodyOverflow;
    this.bodyScrollLocked = false;
  }

  private getFocusableElements(root: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }
}
